import Loader from "../../components/Loader";
import {useQuery} from "@tanstack/react-query";
import Invoice from "./Invoice";
import {useContext, useEffect, useState} from "react";
import Paginator from "../../components/Paginator";
import {Col, Form, FormFeedback, Row, Input, Label, Button} from "reactstrap";
import AuthContext from "../../context/AuthContext";
import handleUnauthorized401QueryError from "../../utils/handleUnauthorized401QueryError";
import {getInvoices, exportInvoiceGST} from "../../api/api";
import {toast} from "react-toastify";
import RecordsNotFound from "../../components/RecordsNotFound";
import {useLocation, useNavigate} from "react-router-dom";


function InvoicePage() {
    const {refreshToken} = useContext(AuthContext)
    const location = useLocation();
    const invoiceOrderFromState = location.state?.invoiceOrder || "";
    const [page, setPage] = useState(1);
    const [invoiceId, setInvoiceId] = useState("");
    const [invoiceOrder, setInvoiceOrder] = useState(invoiceOrderFromState);
    const [invoiceStatus, setInvoiceStatus] = useState("");
    const [invoiceCreatedDate, setInvoiceCreatedDate] = useState("");
    const [invoiceUpdatedDate, setInvoiceUpdatedDate] = useState("");
    const [invoiceURL, setInvoiceURL] = useState("");

    const [queryKey, setQueryKey] = useState(['invoices', page])
    const [gstExportMonth, setGstExportMonth] = useState("");
    const [isExporting, setIsExporting] = useState(false);
    const navigate = useNavigate()

    useEffect(() => {
        setQueryKey(['invoices', page])
    }, [page]);

    useEffect(() => {
        if (!invoiceOrderFromState) return;
        setPage(1);
        setInvoiceId('');
        setInvoiceOrder(invoiceOrderFromState);
        setInvoiceStatus('');
        setInvoiceCreatedDate('');
        setInvoiceUpdatedDate('');
        setInvoiceURL('');
        setQueryKey(['invoices', 1, '', invoiceOrderFromState, '', '', '', '']);
    }, [invoiceOrderFromState]);


    const { status, data, error, isFetching, refetch } = useQuery({
        queryKey: queryKey,
        queryFn: () => getInvoices(page, invoiceId, invoiceOrder, invoiceStatus, invoiceCreatedDate, invoiceUpdatedDate,invoiceURL),
        keepPreviousData : true,
        retry: false,
        onError: (err => {
            handleUnauthorized401QueryError(err, refreshToken, refetch)
            if(err.response.status !== 401){
                toast.error(`An error occurred! ${err?.response?.data?.detail}`)
            }
            if(err.response.status === 403){
                navigate(-1)
            }
        })
        })

    const searchInvoice = (e) =>{
        e.preventDefault()
        setPage(1)
        setQueryKey( ['invoices', page, invoiceId, invoiceOrder, invoiceStatus, invoiceCreatedDate,
            invoiceUpdatedDate,invoiceURL])
    }

    const handleGSTExport = async () => {
        if (!gstExportMonth) {
            toast.error("Please select a month to export.");
            return;
        }
        setIsExporting(true);
        try {
            const [year, month] = gstExportMonth.split('-').map(Number);
            const firstDay = `${year}-${String(month).padStart(2, '0')}-01`;
            const lastDay = new Date(year, month, 0).getDate();
            const lastDayStr = `${year}-${String(month).padStart(2, '0')}-${lastDay}`;
            const response = await exportInvoiceGST(firstDay, lastDayStr);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `GSTR1_Invoices_${gstExportMonth}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Export failed. No invoices found for the selected period.");
        } finally {
            setIsExporting(false);
        }
    };

    const clearSearchInvoice = (e) =>{
        e.preventDefault()
        setInvoiceId('')
        setInvoiceOrder('')
        setInvoiceStatus('')
        setInvoiceCreatedDate('')
        setInvoiceUpdatedDate('')
        setInvoiceURL('')
        setQueryKey( ['invoices', 1])
        // setPage(1)
    }

    return (
        <>
            <div className="card-box">
                <div className="row">
                    <div className="col-sm-12">

                        <Form onSubmit={searchInvoice}>
                            <Row className="form-row align-items-center">
                                <Col>
                                    <Label
                                        className="visually-hidden"
                                        for="id"
                                    >
                                        ID
                                    </Label>
                                    <Input
                                        id="id"
                                        name="id"
                                        type="text"
                                        className="form-control mb-2"
                                        onChange={(e) =>
                                            setInvoiceId(
                                                e.target.value
                                            )
                                        }
                                        value={invoiceId}
                                        invalid={
                                            error && error.response.data.id
                                        }
                                    />
                                    <FormFeedback invalid>
                                        {error && error.response.data.id}
                                    </FormFeedback>
                                </Col>
                                <Col>
                                    <Label
                                        className="visually-hidden"
                                        for="order-id"
                                    >
                                        Order ID
                                    </Label>
                                    <Input
                                        id="order-id"
                                        name="order-id"
                                        type="text"
                                        className="form-control mb-2"
                                        onChange={(e) =>
                                            setInvoiceOrder(
                                                e.target.value
                                            )
                                        }
                                        value={invoiceOrder}
                                        invalid={
                                            error && error.response.data.order
                                        }
                                    />
                                    <FormFeedback invalid>
                                        {error && error.response.data.order}
                                    </FormFeedback>
                                </Col>
                                <Col>
                                    <Label
                                        className="visually-hidden"
                                        for="invoice-paid"
                                    >
                                        Status
                                    </Label>
                                    <Input
                                        type="select"
                                        id="invoice-paid"
                                        className="form-control mb-2"
                                        value={invoiceStatus}
                                        onChange={(e) =>
                                            setInvoiceStatus(
                                                e.target.value
                                            )
                                        }
                                    >
                                        <option value="">--</option>
                                        <option value={"PAID"}>
                                            Paid
                                        </option>
                                        <option value={"UNPAID"}>
                                            Unpaid
                                        </option>
                                        <option value={"CANCELLED"}>
                                            Cancelled
                                        </option>
                                        <option value={"WAIVED-OFF"}>
                                            Waived Off
                                        </option>
                                    </Input>
                                    {/*<FormFeedback invalid>*/}
                                    {/*    {error && error.response.data.description}*/}
                                    {/*</FormFeedback>*/}
                                </Col>
                                <Col>
                                    <Label
                                        className="visually-hidden"
                                        for="invoice-date-created"
                                    >
                                        Date Created
                                    </Label>
                                    <Input
                                        id="invoice-date-created"
                                        name="invoice-date-created"
                                        type="date"
                                        className="form-control mb-2"
                                        onChange={(e) =>
                                            setInvoiceCreatedDate(
                                                e.target.value
                                            )
                                        }
                                        value={invoiceCreatedDate}
                                        invalid={
                                            error && error.response.data.created
                                        }
                                    />
                                    <FormFeedback invalid>
                                        {error && error.response.data.created}
                                    </FormFeedback>
                                </Col>
                                <Col>
                                    <Label
                                        className="visually-hidden"
                                        for="invoice-date-updated"
                                    >
                                        Invoice URL
                                    </Label>
                                    <Input
                                        id="invoice-url"
                                        name="invoice-url"
                                        type="text"
                                        className="form-control mb-2"
                                        onChange={(e) =>
                                            setInvoiceURL(
                                                e.target.value
                                            )
                                        }
                                        value={invoiceURL}
                                        invalid={
                                            error && error.response.data.invoice_url
                                        }
                                    />
                                    <FormFeedback invalid>
                                        {error && error.response.data.invoice_url}
                                    </FormFeedback>
                                </Col>

                            </Row>
                            <Row className="form-row align-items-center">
                                <Col>
                                    <Button type="submit"
                                            className="btn bx-btn-primary waves-effect waves-light mr-2"
                                            disabled={!invoiceId && !invoiceOrder && !invoiceStatus &&
                                            !invoiceCreatedDate && !invoiceURL}
                                    >
                                        Search
                                    </Button>

                                    <Button type="button"
                                            onClick={clearSearchInvoice}
                                            className="btn bx-btn-primary waves-effect waves-light"
                                            disabled={!invoiceId && !invoiceOrder && !invoiceStatus &&
                                            !invoiceCreatedDate && !invoiceURL}
                                    >
                                        Clear
                                    </Button>
                                </Col>
                            </Row>
                        </Form>
                        <div className="d-flex align-items-end mt-3" style={{gap: '10px'}}>
                            <div>
                                <Label for="gst-export-month" className="form-label">GST Export Month</Label>
                                <Input
                                    id="gst-export-month"
                                    type="month"
                                    className="form-control"
                                    value={gstExportMonth}
                                    onChange={(e) => setGstExportMonth(e.target.value)}
                                />
                            </div>
                            <Button
                                type="button"
                                className="btn bx-btn-primary waves-effect waves-light"
                                onClick={handleGSTExport}
                                disabled={!gstExportMonth || isExporting}
                            >
                                {isExporting ? "Exporting..." : "Export GST"}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {status === 'loading' && (
                <Loader size={'default'}/>
            )}

            {status === 'success' | error?.response?.status === 401 && (
                <div className="row">
                    <div className="col-12">

                        <div className="card-box">
                            <div className="table-responsive">
                                <table className="table table-centered mb-0">
                                    <thead className="font-13 bg-light text-muted">
                                    <tr>
                                        <th className="font-weight-medium">Invoice Number</th>
                                        <th className="font-weight-medium">Customer Email</th>
                                        <th className="font-weight-medium">Short Invoice URL</th>
                                        <th className="font-weight-medium">Line Items</th>
                                        <th className="font-weight-medium">Total Tax</th>
                                        <th className="font-weight-medium">Amount Total</th>
                                        <th className="font-weight-medium">Status</th>
                                        <th className="font-weight-medium">Created</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {data?.count > 0? data?.results?.map(invoice => (
                                        <Invoice key={invoice.id} invoice={invoice}/>
                                    )): <RecordsNotFound/>}
                                    </tbody>
                                </table>
                            </div>
                            {status === 'success' && (
                                <Paginator data={data} page={page} setPage={setPage} last={data?.last_page} loading={isFetching}/>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default InvoicePage;
